import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  Plus,
  Search,
  Filter,
  UserCheck,
  UserX,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Car,
  User,
  Users,
  X,
  Save
} from 'lucide-react';

const Visitantes = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [visitantes, setVisitantes] = useState([]);
  const [apartamentos, setApartamentos] = useState([]);
  const [moradores, setMoradores] = useState([]);
  const [moradoresFiltrados, setMoradoresFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVisitante, setEditingVisitante] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apartamentoSelecionado, setApartamentoSelecionado] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [formData, setFormData] = useState(() => {
    // Inicializar apartamento_id para não-administradores
    let apartamentoId = '';
    if (!isAdministrador() && user?.vinculos) {
      const vinculoAtivo = user.vinculos.find(v => v.condominio_id === getCondominioId());
      apartamentoId = vinculoAtivo?.apartamento_id || '';
    }

    return {
      visitante_nome: '',
      visitante_documento_tipo: 'nao_informado',
      visitante_documento_numero: '',
      visitante_telefone: '',
      apartamento_id: apartamentoId,
      morador_responsavel_id: '',
      visitante_motivo: '',
      visitante_tipo_periodo: false,
      visitante_data_visita: new Date().toISOString().split('T')[0],
      visitante_data_inicio_periodo: '',
      visitante_data_fim_periodo: '',
      visitante_tem_veiculo: false,
      visitante_veiculo_modelo: '',
      visitante_veiculo_placa: '',
      visitante_veiculo_cor: '',
      visitante_observacoes: ''
    };
  });

  useEffect(() => {
    const condominioId = getCondominioId();
    if (condominioId) {
      loadVisitantes();
      loadApartamentos();
      
      // Se não for administrador, carregar apenas moradores do seu apartamento
      if (!isAdministrador() && user?.vinculos) {
        const vinculoAtivo = user.vinculos.find(v => v.condominio_id === condominioId);
        if (vinculoAtivo?.apartamento_id) {
          loadMoradoresPorApartamento(vinculoAtivo.apartamento_id);
        }
      } else {
        loadMoradores();
      }
    }
  }, [condominioSelecionado, filters, pagination.page]);

  // Inicializar apartamento selecionado para não-administradores quando o modal abre
  React.useEffect(() => {
    console.log('🔄 useEffect Modal - showModal:', showModal);
    console.log('🔄 isAdministrador():', isAdministrador());
    console.log('🔄 user?.vinculos:', user?.vinculos);
    console.log('🔄 editingVisitante:', editingVisitante);
    
    if (showModal && !isAdministrador() && user?.vinculos && !editingVisitante) {
      const condominioId = getCondominioId();
      console.log('🔄 Buscando vínculo para condomínio:', condominioId);
      const vinculoAtivo = user.vinculos.find(v => v.condominio_id === condominioId);
      console.log('🔄 Vínculo encontrado:', vinculoAtivo);
      
      if (vinculoAtivo?.apartamento_id) {
        setApartamentoSelecionado(vinculoAtivo.apartamento_id.toString());
        console.log('🏢 Apartamento inicializado para morador:', vinculoAtivo.apartamento_id);
        
        // Carregar moradores do apartamento quando abrir o modal
        console.log('🔄 Chamando loadMoradoresPorApartamento com ID:', vinculoAtivo.apartamento_id);
        loadMoradoresPorApartamento(vinculoAtivo.apartamento_id);
      } else {
        console.log('❌ Nenhum apartamento_id encontrado no vínculo');
      }
    }
  }, [showModal, isAdministrador, user, editingVisitante]);

  // Filtrar moradores quando o apartamento mudar
  React.useEffect(() => {
    if (apartamentoSelecionado && moradores) {
      const moradoresFilt = moradores.filter(
        m => m.apartamento_id === parseInt(apartamentoSelecionado) || m.id === parseInt(apartamentoSelecionado)
      );
      setMoradoresFiltrados(moradoresFilt);
      console.log('🏢 Moradores filtrados do apartamento', apartamentoSelecionado, ':', moradoresFilt.length);
      console.log('📋 Lista de moradores completa:', moradores.length);
      console.log('📋 Moradores filtrados:', moradoresFilt);
    } else if (!apartamentoSelecionado) {
      setMoradoresFiltrados([]);
    }
  }, [apartamentoSelecionado, moradores]);

  const loadVisitantes = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      console.log('🔍 Carregando visitantes - Condomínio ID:', condominioId);
      if (!condominioId) {
        setVisitantes([]);
        return;
      }
      
      const params = new URLSearchParams();
      params.append('condominio_id', condominioId);
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/visitantes?${params}`);
      const data = response.data.data;
      setVisitantes(data.visitantes || []);
      const pag = data.pagination || {};
      setPagination(prev => ({ ...prev, total: pag.total || 0, pages: Math.max(1, pag.pages || 1) }));
    } catch (error) {
      console.error('❌ Erro ao carregar visitantes:', error);
      console.error('❌ Response:', error.response);
    } finally {
      setLoading(false);
    }
  };

  const loadApartamentos = async () => {
    try {
      const condominioId = getCondominioId();
      if (!condominioId) {
        setApartamentos([]);
        return;
      }
      
      const response = await api.get(`/apartamentos?condominio_id=${condominioId}`);
      setApartamentos(response.data.data.apartamentos || []);
    } catch (error) {
      console.error('Erro ao carregar apartamentos:', error);
    }
  };

  const loadMoradores = async () => {
    try {
      const condominioId = getCondominioId();
      if (!condominioId) {
        setMoradores([]);
        return;
      }
      
      const response = await api.get(`/moradores?condominio_id=${condominioId}`);
      setMoradores(response.data.data.moradores || []);
    } catch (error) {
      console.error('Erro ao carregar moradores:', error);
    }
  };

  const loadMoradoresPorApartamento = async (apartamentoId) => {
    try {
      console.log('🔍 loadMoradoresPorApartamento - Apartamento ID:', apartamentoId);
      if (!apartamentoId) {
        setMoradores([]);
        return;
      }
      
      const response = await api.get(`/moradores?apartamento_id=${apartamentoId}`);
      console.log('✅ Moradores do apartamento:', response.data);
      console.log('📋 Total de moradores:', response.data.data.moradores?.length || 0);
      console.log('👥 Moradores:', response.data.data.moradores);
      setMoradores(response.data.data.moradores || []);
    } catch (error) {
      console.error('❌ Erro ao carregar moradores do apartamento:', error);
      console.error('❌ Response:', error.response);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);
    
    try {
      const dadosParaEnvio = {
        ...formData,
        condominio_id: getCondominioId(),
        visitante_tipo: formData.visitante_tipo_periodo ? 'periodo' : 'diaria'
      };
      
      // Remover campos de documento se não informado
      if (formData.visitante_documento_tipo === 'nao_informado') {
        delete dadosParaEnvio.visitante_documento_tipo;
        delete dadosParaEnvio.visitante_documento_numero;
      }
      
      const response = await api.post('/visitantes', dadosParaEnvio);
      
      // Mostrar mensagem de sucesso
      if (window.showNotification) {
        const message = response.data.message || 'Visitante registrado com sucesso';
        window.showNotification(message, 'success');
      }
      
      setShowModal(false);
      setEditingVisitante(null);
      resetForm();
      loadVisitantes();
    } catch (error) {
      console.error('Erro ao salvar visitante:', error);
      
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        setErrorMessage(errorMessages);
      } else {
        setErrorMessage('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (visitante) => {
    setEditingVisitante(visitante);
    setFormData({
      visitante_nome: visitante.visitante_nome || '',
      visitante_documento_tipo: visitante.visitante_documento_tipo || 'nao_informado',
      visitante_documento_numero: visitante.visitante_documento_numero || '',
      visitante_telefone: visitante.visitante_telefone || '',
      apartamento_id: visitante.apartamento_id || '',
      morador_responsavel_id: visitante.morador_responsavel_id || '',
      visitante_motivo: visitante.visitante_motivo || '',
      visitante_tipo_periodo: visitante.visitante_tipo === 'periodo',
      visitante_data_visita: visitante.visitante_data_visita ? visitante.visitante_data_visita.split('T')[0] : new Date().toISOString().split('T')[0],
      visitante_data_inicio_periodo: visitante.visitante_data_inicio_periodo ? visitante.visitante_data_inicio_periodo.split('T')[0] : '',
      visitante_data_fim_periodo: visitante.visitante_data_fim_periodo ? visitante.visitante_data_fim_periodo.split('T')[0] : '',
      visitante_tem_veiculo: visitante.visitante_tem_veiculo || false,
      visitante_veiculo_modelo: visitante.visitante_veiculo_modelo || '',
      visitante_veiculo_placa: visitante.visitante_veiculo_placa || '',
      visitante_veiculo_cor: visitante.visitante_veiculo_cor || '',
      visitante_observacoes: visitante.visitante_observacoes || ''
    });
    
    // Carregar moradores do apartamento selecionado
    if (visitante.apartamento_id) {
      loadMoradoresPorApartamento(visitante.apartamento_id);
    }
    
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este visitante?')) {
      try {
        await api.delete(`/visitantes/${id}`);
        loadVisitantes();
      } catch (error) {
        console.error('Erro ao excluir visitante:', error);
      }
    }
  };

  const handleRegistrarEntrada = async (id, nome) => {
    if (window.confirm(`Confirmar entrada de ${nome}?`)) {
      try {
        await api.put(`/visitantes/${id}/registrar-entrada`);
        if (window.showNotification) {
          window.showNotification('Entrada registrada com sucesso!', 'success');
        }
        loadVisitantes();
      } catch (error) {
        console.error('Erro ao registrar entrada:', error);
        if (window.showNotification) {
          const message = error.response?.data?.message || 'Erro ao registrar entrada';
          window.showNotification(message, 'error');
        }
      }
    }
  };

  const handleRegistrarSaida = async (id, nome) => {
    if (window.confirm(`Confirmar saída de ${nome}?`)) {
      try {
        await api.put(`/visitantes/${id}/registrar-saida`);
        if (window.showNotification) {
          window.showNotification('Saída registrada com sucesso!', 'success');
        }
        loadVisitantes();
      } catch (error) {
        console.error('Erro ao registrar saída:', error);
        if (window.showNotification) {
          const message = error.response?.data?.message || 'Erro ao registrar saída';
          window.showNotification(message, 'error');
        }
      }
    }
  };

  const resetForm = () => {
    // Se for morador, preencher automaticamente o apartamento
    let apartamentoId = '';
    if (!isAdministrador() && user?.vinculos) {
      const condominioId = getCondominioId();
      const vinculoAtivo = user.vinculos.find(v => v.condominio_id === condominioId);
      if (vinculoAtivo?.apartamento_id) {
        apartamentoId = vinculoAtivo.apartamento_id;
      }
    }

    setFormData({
      visitante_nome: '',
      visitante_documento_tipo: 'nao_informado',
      visitante_documento_numero: '',
      visitante_telefone: '',
      apartamento_id: apartamentoId,
      morador_responsavel_id: '',
      visitante_motivo: '',
      visitante_tipo_periodo: false,
      visitante_data_visita: new Date().toISOString().split('T')[0],
      visitante_data_inicio_periodo: '',
      visitante_data_fim_periodo: '',
      visitante_tem_veiculo: false,
      visitante_veiculo_modelo: '',
      visitante_veiculo_placa: '',
      visitante_veiculo_cor: '',
      visitante_observacoes: ''
    });
    setMoradores([]);
    setErrorMessage('');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      aguardando: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Aguardando' },
      autorizado: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Autorizado' },
      em_visita: { color: 'bg-blue-100 text-blue-800', icon: UserCheck, text: 'Em Visita' },
      finalizado: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle, text: 'Finalizado' },
      negado: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Negado' }
    };
    
    const statusInfo = statusMap[status] || statusMap.aguardando;
    const Icon = statusInfo.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {statusInfo.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const applyPhoneMask = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      if (numbers.length <= 10) {
        return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
      } else {
        return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
      }
    }
    return value;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    if (name === 'visitante_telefone') {
      processedValue = applyPhoneMask(value);
    }
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : processedValue
      };
      
      // Se o apartamento foi alterado, carregar moradores desse apartamento
      if (name === 'apartamento_id') {
        setApartamentoSelecionado(processedValue);
        loadMoradoresPorApartamento(processedValue);
        // Limpar o morador selecionado quando mudar o apartamento
        newData.morador_responsavel_id = '';
      }
      
      // Se o tipo de documento foi alterado e está vazio, limpar o número
      if (name === 'visitante_documento_tipo' && !processedValue) {
        newData.visitante_documento_numero = '';
      }
      
      // Se o checkbox de período foi alterado, definir datas padrão
      if (name === 'visitante_tipo_periodo') {
        if (checked) {
          // Se marcou período, definir data de início como hoje e fim como amanhã
          const hoje = new Date();
          const amanha = new Date();
          amanha.setDate(amanha.getDate() + 1);
          newData.visitante_data_inicio_periodo = hoje.toISOString().split('T')[0];
          newData.visitante_data_fim_periodo = amanha.toISOString().split('T')[0];
        } else {
          // Se desmarcou período, limpar as datas de período
          newData.visitante_data_inicio_periodo = '';
          newData.visitante_data_fim_periodo = '';
        }
      }
      
      return newData;
    });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Visitantes</h1>
        </div>
        {!isAdministrador() || getCondominioId() ? (
          <button
            onClick={() => {
              setEditingVisitante(null);
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Visitante
          </button>
        ) : null}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="form-input"
              >
                <option value="">Todos os status</option>
                <option value="aguardando">Aguardando</option>
                <option value="autorizado">Autorizado</option>
                <option value="em_visita">Em Visita</option>
                <option value="finalizado">Finalizado</option>
                <option value="negado">Negado</option>
              </select>
            </div>
            <div>
              <label className="form-label">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nome, documento..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="form-input pl-10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Visitantes */}
      <div className="card">
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visitante</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Apartamento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo/Período</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Veículo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hora Chegada</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hora Saída</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visitantes.map((visitante) => (
                  <tr key={visitante.visitante_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {visitante.visitante_nome}
                        </div>
                        <div className="text-sm text-gray-500">
                          {visitante.visitante_telefone || 'Sem telefone'}
                        </div>
                        {visitante.visitante_documento_tipo && visitante.visitante_documento_numero && (
                          <div className="text-xs text-gray-400">
                            {visitante.visitante_documento_tipo.toUpperCase()}: {visitante.visitante_documento_numero}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {visitante.apartamento ? 
                        `${visitante.apartamento.apartamento_bloco} - ${visitante.apartamento.apartamento_numero}` : 
                        'N/A'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          visitante.visitante_tipo === 'diaria' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {visitante.visitante_tipo === 'diaria' ? 'Diária' : 'Período'}
                        </span>
                      </div>
                      {visitante.visitante_tipo === 'diaria' ? (
                        <div className="text-xs text-gray-400 mt-1">
                          {visitante.visitante_data_visita ? 
                            new Date(visitante.visitante_data_visita).toLocaleDateString('pt-BR') : 
                            'Data não informada'
                          }
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 mt-1">
                          {visitante.visitante_data_inicio_periodo && visitante.visitante_data_fim_periodo ? (
                            <>
                              {new Date(visitante.visitante_data_inicio_periodo).toLocaleDateString('pt-BR')} - {' '}
                              {new Date(visitante.visitante_data_fim_periodo).toLocaleDateString('pt-BR')}
                            </>
                          ) : (
                            'Período não informado'
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {visitante.visitante_tem_veiculo ? (
                        <div className="flex items-center">
                          <Car className="h-4 w-4 text-green-600 mr-1" />
                          <div>
                            <div className="font-medium">{visitante.visitante_veiculo_modelo}</div>
                            <div className="text-xs">{visitante.visitante_veiculo_placa}</div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">Sem veículo</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(visitante.visitante_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {visitante.visitante_hora_chegada_real ? (
                        <div>
                          <div className="font-medium text-green-600">
                            {new Date(visitante.visitante_hora_chegada_real).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          <div className="text-xs text-gray-400">
                            {new Date(visitante.visitante_hora_chegada_real).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">Não chegou</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {visitante.visitante_hora_saida_real ? (
                        <div>
                          <div className="font-medium text-red-600">
                            {new Date(visitante.visitante_hora_saida_real).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          <div className="text-xs text-gray-400">
                            {new Date(visitante.visitante_hora_saida_real).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">Ainda presente</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {/* Botão Registrar Entrada - apenas se ainda não chegou */}
                        {!visitante.visitante_hora_chegada_real && visitante.visitante_status !== 'negado' && (
                          <button
                            onClick={() => handleRegistrarEntrada(visitante.visitante_id, visitante.visitante_nome)}
                            className="text-green-600 hover:text-green-900"
                            title="Registrar Entrada"
                          >
                            <UserCheck className="h-4 w-4" />
                          </button>
                        )}
                        {/* Botão Registrar Saída - apenas se já chegou mas ainda não saiu */}
                        {visitante.visitante_hora_chegada_real && !visitante.visitante_hora_saida_real && (
                          <button
                            onClick={() => handleRegistrarSaida(visitante.visitante_id, visitante.visitante_nome)}
                            className="text-red-600 hover:text-red-900"
                            title="Registrar Saída"
                          >
                            <UserX className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(visitante)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Visualizar"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container custom-scrollbar">
            <div className="modal-header">
              <h3 className="modal-title">
                {editingVisitante ? 'Editar Visitante' : 'Novo Visitante'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingVisitante(null);
                  resetForm();
                }}
                className="modal-close-btn"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Exibir mensagem de erro */}
            {errorMessage && (
              <div className="alert alert-error">
                <svg className="alert-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="alert-content">
                  <h3 className="alert-title">Erro ao salvar visitante</h3>
                  <p className="alert-message">{errorMessage}</p>
                </div>
              </div>
            )}
              
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h4 className="form-section-title">Dados Pessoais</h4>
                <div className="form-grid form-grid-2">
                  <div className="form-field">
                    <label className="form-label form-label-required">Nome</label>
                    <input
                      type="text"
                      name="visitante_nome"
                      value={formData.visitante_nome}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                      placeholder="Digite o nome do visitante"
                    />
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label">Telefone</label>
                    <input
                      type="text"
                      name="visitante_telefone"
                      value={formData.visitante_telefone}
                      onChange={handleInputChange}
                      placeholder="(11) 99999-9999"
                      maxLength="15"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label">Tipo de Documento</label>
                    <select
                      name="visitante_documento_tipo"
                      value={formData.visitante_documento_tipo}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="nao_informado">Não informado</option>
                      <option value="cpf">CPF</option>
                      <option value="rg">RG</option>
                      <option value="cnh">CNH</option>
                      <option value="passaporte">Passaporte</option>
                    </select>
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label">Número do Documento</label>
                    <input
                      type="text"
                      name="visitante_documento_numero"
                      value={formData.visitante_documento_numero}
                      onChange={handleInputChange}
                      className="form-input"
                      disabled={formData.visitante_documento_tipo === 'nao_informado'}
                      placeholder="Digite o número"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4 className="form-section-title">Dados da Visita</h4>
                <div className="form-grid form-grid-2">
                  <div className="form-field">
                    <label className="form-label form-label-required">Apartamento</label>
                    {!isAdministrador() ? (
                      // Não-administradores só podem cadastrar visitantes para seu apartamento
                      <>
                        <input
                          type="hidden"
                          name="apartamento_id"
                          value={user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id || formData.apartamento_id}
                        />
                        <input
                          type="text"
                          value={(() => {
                            const aptId = user?.vinculos?.find(v => v.condominio_id === getCondominioId())?.apartamento_id || formData.apartamento_id;
                            const apt = apartamentos.find(a => a.apartamento_id === aptId || a.apartamento_id === parseInt(aptId));
                            console.log('🏢 Buscando apartamento ID:', aptId);
                            console.log('🏢 Apartamentos disponíveis:', apartamentos.length);
                            console.log('🏢 Apartamento encontrado:', apt);
                            return apt ? `${apt.apartamento_bloco} - ${apt.apartamento_numero}` : 'Carregando...';
                          })()}
                          readOnly
                          className="form-input"
                          title="Como morador, você só pode cadastrar visitantes para seu apartamento"
                        />
                      </>
                    ) : (
                      // Administradores podem escolher qualquer apartamento
                      <select
                        name="apartamento_id"
                        value={formData.apartamento_id}
                        onChange={handleInputChange}
                        required
                        className="form-select"
                      >
                        <option value="">Selecione um apartamento</option>
                        {apartamentos.map((apartamento) => (
                          <option key={apartamento.apartamento_id} value={apartamento.apartamento_id}>
                            {apartamento.apartamento_bloco} - {apartamento.apartamento_numero}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  <div className="form-field">
                    <label className="form-label form-label-required">Morador Responsável</label>
                    {(() => {
                      console.log('🔍 SELECT MORADOR - isAdministrador():', isAdministrador());
                      console.log('🔍 apartamentoSelecionado:', apartamentoSelecionado);
                      console.log('🔍 formData.apartamento_id:', formData.apartamento_id);
                      console.log('🔍 moradores.length:', moradores.length);
                      console.log('🔍 moradoresFiltrados.length:', moradoresFiltrados.length);
                      console.log('🔍 Vai usar:', isAdministrador() && apartamentoSelecionado ? 'moradoresFiltrados' : 'moradores');
                      return null;
                    })()}
                    <select
                      name="morador_responsavel_id"
                      value={formData.morador_responsavel_id}
                      onChange={handleInputChange}
                      required
                      className="form-select"
                      disabled={!formData.apartamento_id}
                    >
                      <option value="">
                        {formData.apartamento_id ? 'Selecione um morador' : 'Primeiro selecione um apartamento'}
                      </option>
                      {(isAdministrador() && apartamentoSelecionado ? moradoresFiltrados : moradores).map((morador) => (
                        <option key={morador.id || morador.morador_id} value={morador.id || morador.morador_id}>
                          {morador.nome || morador.morador_nome}
                        </option>
                      ))}
                    </select>
                    {isAdministrador() && apartamentoSelecionado && moradoresFiltrados.length === 0 && (
                      <p className="form-error-message" style={{color: '#6b7280'}}>
                        Nenhum morador cadastrado neste apartamento
                      </p>
                    )}
                    {!isAdministrador() && moradores.length === 0 && (
                      <p className="form-error-message" style={{color: '#6b7280'}}>
                        Nenhum morador cadastrado no seu apartamento
                      </p>
                    )}
                  </div>
                  
                  <div className="form-field form-field-full">
                    <label className="form-label">Motivo da Visita</label>
                    <input
                      type="text"
                      name="visitante_motivo"
                      value={formData.visitante_motivo}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Descreva o motivo da visita"
                    />
                  </div>
                  
                  <div className="form-field form-field-full">
                    <label className="form-label" style={{marginBottom: 0}}>
                      <input
                        type="checkbox"
                        name="visitante_tipo_periodo"
                        checked={formData.visitante_tipo_periodo}
                        onChange={handleInputChange}
                        className="form-checkbox"
                      />
                      {' '}Visita por período (ex: reforma, prestação de serviços)
                    </label>
                  </div>
                  
                  {!formData.visitante_tipo_periodo ? (
                    <div className="form-field">
                      <label className="form-label form-label-required">Data da Visita</label>
                      <input
                        type="date"
                        name="visitante_data_visita"
                        value={formData.visitante_data_visita}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="form-field">
                        <label className="form-label form-label-required">Data de Início</label>
                        <input
                          type="date"
                          name="visitante_data_inicio_periodo"
                          value={formData.visitante_data_inicio_periodo}
                          onChange={handleInputChange}
                          required
                          className="form-input"
                        />
                      </div>
                      <div className="form-field">
                        <label className="form-label form-label-required">Data de Fim</label>
                        <input
                          type="date"
                          name="visitante_data_fim_periodo"
                          value={formData.visitante_data_fim_periodo}
                          onChange={handleInputChange}
                          required
                          className="form-input"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="form-section">
                <h4 className="form-section-title">
                  <Car className="h-5 w-5" />
                  Dados do Veículo
                </h4>
                <div className="form-grid form-grid-2">
                  <div className="form-field form-field-full">
                    <label className="form-label" style={{marginBottom: 0}}>
                      <input
                        type="checkbox"
                        name="visitante_tem_veiculo"
                        checked={formData.visitante_tem_veiculo}
                        onChange={handleInputChange}
                        className="form-checkbox"
                      />
                      {' '}Visitante vai entrar com veículo
                    </label>
                  </div>
                  
                  {formData.visitante_tem_veiculo && (
                    <>
                      <div className="form-field">
                        <label className="form-label">Modelo do Veículo</label>
                        <input
                          type="text"
                          name="visitante_veiculo_modelo"
                          value={formData.visitante_veiculo_modelo}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Ex: Gol, Civic, etc"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label className="form-label">Placa</label>
                        <input
                          type="text"
                          name="visitante_veiculo_placa"
                          value={formData.visitante_veiculo_placa}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="ABC-1234"
                        />
                      </div>
                      
                      <div className="form-field">
                        <label className="form-label">Cor</label>
                        <input
                          type="text"
                          name="visitante_veiculo_cor"
                          value={formData.visitante_veiculo_cor}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Ex: Preto, Branco, etc"
                        />
                      </div>
                    </>
                  )}
                  
                  <div className="form-field form-field-full">
                    <label className="form-label">Observações</label>
                    <textarea
                      name="visitante_observacoes"
                      value={formData.visitante_observacoes}
                      onChange={handleInputChange}
                      rows={3}
                      className="form-textarea"
                      placeholder="Digite observações sobre a visita..."
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingVisitante(null);
                    resetForm();
                  }}
                  className="btn-base btn-secondary-custom"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-base btn-primary-custom btn-icon"
                >
                  <Save className="h-4 w-4" />
                  {isSubmitting ? 'Salvando...' : (editingVisitante ? 'Atualizar' : 'Registrar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Visitantes;
